import { NavLink, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Wallet,
  MapPin,
  CalendarClock,
  ListChecks,
  CreditCard,
  Users,
  LogOut,
  Sparkles,
  CheckCircle,
  Mail,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/finans", label: "Gelir / Gider", icon: Wallet, testid: "nav-finance" },
  { to: "/sahalar", label: "Saha Sahipleri", icon: MapPin, testid: "nav-fields" },
  { to: "/slotlar", label: "Slot Takibi", icon: CalendarClock, testid: "nav-slots" },
  { to: "/odemeler", label: "Saha Ödemeleri", icon: CreditCard, testid: "nav-payments" },
  { to: "/gorevler", label: "Görevler", icon: ListChecks, testid: "nav-todos" },
  { to: "/eposta", label: "E-Posta Otomasyonu", icon: Mail, testid: "nav-email" },
  { to: "/uyeler", label: "Üyeler", icon: Users, testid: "nav-members", adminOnly: true },
];


export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navItems = NAV.filter((item) => !item.adminOnly || user?.role === "admin");

  return (
    <div className="min-h-screen flex bg-[#090d16] text-foreground mesh-bg">
      {/* Modern Glassmorphism Sidebar */}
      <aside className="hidden md:flex flex-col w-72 shrink-0 border-r border-slate-800/80 bg-slate-950/70 backdrop-blur-xl sticky top-0 h-screen z-40">
        <div className="p-6 border-b border-slate-800/60 flex flex-col items-center text-center gap-3">
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur opacity-40 group-hover:opacity-75 transition duration-500"></div>
            <div className="relative w-20 h-20 rounded-full bg-slate-900 p-1 ring-2 ring-emerald-500/30">
              <img src="/assets/logo-round.webp" alt="10-11" className="w-full h-full object-cover rounded-full" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1.5">
              <p className="font-anton text-2xl tracking-wider text-white">10-11</p>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">PRO</span>
            </div>
            <span className="text-[10px] uppercase tracking-[0.35em] text-emerald-500/90 font-medium">Yönetim Platformu</span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-emerald-600 to-emerald-500 text-white shadow-[0_4px_20px_-4px_rgba(16,185,129,0.5)] font-semibold"
                      : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px]" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800/60 bg-slate-900/40">
          <div className="flex items-center gap-3 px-3 py-2.5 mb-3 rounded-xl bg-slate-800/40 border border-slate-800/60">
            <div className="relative">
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-sm font-bold shrink-0 border border-emerald-500/30">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-slate-950"></span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-200 truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-button"
            onClick={logout}
            className="w-full justify-start text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl text-xs h-9"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop Top Header Bar */}
        <header className="hidden md:flex items-center justify-between px-8 py-4 border-b border-slate-800/60 bg-slate-950/40 backdrop-blur-md sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-medium text-slate-300">10-11.com.tr Canlı Entegre</span>
            <span className="text-slate-700">|</span>
            <span className="text-xs text-slate-400">Canlı Sistem & Yönetim Paneli</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" /> Canlı Senkronizasyon Aktif
            </div>
          </div>
        </header>

        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/80 flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-white p-0.5 ring-1 ring-emerald-500/40">
              <img src="/assets/logo-round.webp" alt="10-11" className="w-full h-full object-cover rounded-full" />
            </div>
            <div>
              <span className="font-anton text-base tracking-wide text-white">10-11</span>
              <span className="text-[9px] uppercase tracking-widest text-emerald-400 block -mt-1">Yönetim</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="mobile-logout" className="text-slate-400 hover:text-rose-400">
            <LogOut className="w-4 h-4" />
          </Button>
        </header>

        {/* Mobile nav */}
        <nav className="md:hidden flex overflow-x-auto gap-2 px-4 py-3 border-b border-slate-800/80 bg-slate-950/90 no-scrollbar">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  isActive ? "bg-emerald-600 text-white font-semibold shadow-md shadow-emerald-600/30" : "bg-slate-900 text-slate-400"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-1 w-full px-4 sm:px-8 lg:px-10 py-6 sm:py-8"
        >
          <div className="max-w-[1240px] mx-auto space-y-6">
            <Outlet />
          </div>
        </motion.main>
      </div>
    </div>
  );
}

